// Pure, dependency-free client-side BIC derivation/autofill decision logic for the SEPA
// signature form's onSepaIbanBlur handler (resources/views/account/sign-pdf-attachment.blade.php).
//
// Mirrors App\Services\Iban\DutchBicResolver::resolve() exactly (bank identifier at positions
// 5-8 of an 18-character canonical NL IBAN), reading from the SAME config source
// (config('iban.nl_bic_map'), embedded into the page's own Vue data as `nlBicMap` - see
// AddressEnrichmentService-adjacent design notes) rather than a second, driftable copy of the
// map. No network call is ever made - this is a pure local lookup, exactly like the backend
// resolver it mirrors.
//
// Mirrors addressEnrichmentAutofill.js's own precedence convention: a provider-derived value is
// a suggestion only, never overwrites a value the user (or a prior explicit edit) already owns.

const NL_LENGTH = 18;
const BANK_IDENTIFIER_START = 4;
const BANK_IDENTIFIER_LENGTH = 4;

/**
 * @param {string} canonicalIban an already-normalized (trim/strip-spaces/uppercase) IBAN
 * @param {Object<string, string>} bicMap the page's own `nlBicMap` data (config('iban.nl_bic_map'))
 * @returns {string|null} the derived BIC, or null if not a well-formed 18-char NL IBAN, or the
 *   bank identifier isn't in the map - never fabricates a BIC.
 */
export function deriveBicForIban(canonicalIban, bicMap) {
  if (typeof canonicalIban !== 'string' || canonicalIban.length !== NL_LENGTH) {
    return null;
  }
  if (canonicalIban.slice(0, 2) !== 'NL') {
    return null;
  }
  if (!bicMap || typeof bicMap !== 'object') {
    return null;
  }

  const bankIdentifier = canonicalIban.slice(BANK_IDENTIFIER_START, BANK_IDENTIFIER_START + BANK_IDENTIFIER_LENGTH);
  if (bankIdentifier.length !== BANK_IDENTIFIER_LENGTH || !/^[A-Z]+$/.test(bankIdentifier)) {
    return null;
  }

  return bicMap[bankIdentifier] || null;
}

/**
 * Decides whether a freshly derived BIC should be applied, following the same fill-vs-skip
 * precedence table as applyEnrichmentToState(): a BIC the user has manually edited (or that was
 * never provider-derived to begin with, e.g. an existing persisted value) is never overwritten.
 *
 * @param {{bic: ?string, bicProviderOwned: boolean}} state
 * @param {string|null} derivedBic
 * @returns {{bic: ?string, bicProviderOwned: boolean}}
 */
export function applyBicAutofill(state, derivedBic) {
  if (derivedBic === null || derivedBic === undefined) {
    return { bic: state.bic, bicProviderOwned: state.bicProviderOwned };
  }
  if (state.bicProviderOwned === false) {
    // Manually edited (or already provider-un-owned for some other reason) - never overwritten.
    return { bic: state.bic, bicProviderOwned: false };
  }

  return { bic: derivedBic, bicProviderOwned: true };
}

/**
 * Distinguishes a genuine signatory keystroke on the BIC field from resources/js/admin/mixins/
 * field.js's own mixin re-emitting 'input' whenever the field's bound value changes for ANY
 * reason - including a parent-driven (programmatic autofill) assignment, not only real typing.
 * That mixin's `model` watcher unconditionally calls $emit('input', value) regardless of source,
 * so a plain autofill write to a v-model/`:value`-bound field echoes straight back through the
 * exact same @input listener the signatory's own keystrokes use.
 *
 * Comparing the incoming value against the last value the PARENT itself wrote programmatically
 * lets the @input handler skip taking BIC ownership away from autofill on that echo, without
 * missing a real edit (a genuine keystroke either differs from the last autofilled value, or -
 * the one narrow, harmless edge case - exactly repeats it, in which case ownership is taken on
 * the very next differing keystroke instead).
 *
 * @param {string} incomingValue value arriving at the @input handler
 * @param {?string} lastAppliedValue the last value the parent itself wrote via autofill, or null
 *   if no autofill has happened yet
 * @returns {boolean} true if this is the mixin's own echo of our last autofill, not a real edit
 */
export function isAutofillEcho(incomingValue, lastAppliedValue) {
  return lastAppliedValue !== null && incomingValue === lastAppliedValue;
}

export default { deriveBicForIban, applyBicAutofill, isAutofillEcho };
